import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, NgModel, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { compress, decompress } from 'lz-string';
import { BehaviorSubject, Observable, Subject, startWith, map, takeUntil, filter, merge, tap, firstValueFrom, combineLatest, take } from 'rxjs';
import { Alert, alertActivity, AlertInDatabase, AlertPostBody, MsgType, MsgTypeOptions } from 'src/app/Interfaces/alerts';
import { Users } from 'src/app/Interfaces/users';
import { AlertService } from 'src/app/services/alert.service';
import { StoreService } from 'src/app/services/store.service';
import { AtLeast } from 'src/app/Interfaces/util';
import { LoadingService } from 'src/app/services/loading.service';
import { AllClients, DocTypeDefinitions } from 'src/app/Interfaces/SQL-morning-responses';
import { ValidateArrayLength } from 'src/app/Utility/FormValidators';
import { ResponseFromExpress } from 'src/app/Interfaces/SQL-interfaces';
import { UtilService } from 'src/app/services/util.service';
import { genericSearchingFunctionWithoutNullChecks } from 'src/app/Utility/functions';
import { DoctypeService } from 'src/app/services/doctype.service';
import { environment } from 'src/environments/environment';
import { BottomPopupService } from 'src/app/services/bottom-popup.service';
import { displayFn } from '@utils/display_functions/displayFn';

// type MsgText = typeof text[number];
const activeArray = ['0', '1', '2'] as const;

const YEAR = 31_104_000_000;
const minDate = new Date();
const maxDate = new Date(new Date().getTime() * YEAR * 10); // 10 years max

const formDefaultValue = {
	Name: '',
	Msg: '',
	Active: <typeof activeArray[number]>alertActivity.active,
	time: '',
	clients: [],
	clientsSearch: '',
	agents: [],
	agentsSearch: '',
	doctypes: [],
	doctypesSearch: '',
	percentagePerTeuda: <number>null,
	shouldAffectAllClients: true,
	shouldAffectAllAgents: true,
	shouldAffectAllDoctypes: true,
	shouldBlockAgent: false,
	StartDate: minDate,
	EndDate: minDate,
};

type ArrayValues = 'agents' | 'clients' | 'doctypes';

@Component({
	selector: 'app-alert-manager',
	templateUrl: './alert-manager.component.html',
	styleUrls: ['./alert-manager.component.scss'],
})
export class AlertManagerComponent implements OnInit, OnDestroy {
	private _ss = inject(StoreService);
	private router = inject(Router);
	private fb = inject(FormBuilder);
	private _alert = inject(AlertService);
	private acr = inject(ActivatedRoute);
	private _loading = inject(LoadingService);
	private _doctype = inject(DoctypeService);
	private _bottomPopup = inject(BottomPopupService);

	constructor() {}

	allAlerts: Alert[] = this._alert.Alerts;
	allAlerts$ = new BehaviorSubject<Alert[]>(this.allAlerts);

	inputControls = new Array(this.allAlerts.length).fill(null);

	loading = false;

	alertSuccess = false;

	MsgTypeText = Object.values(MsgType).reduce((p, c) => {
		p[c.msgType] = c.msgText;
		return p;
	}, {});
	MsgTypeOptions = MsgTypeOptions;

	year = 31_104_000_000;

	dates = { min: minDate, max: maxDate };

	destroy$: Subject<void> = new Subject();

	private _filter = genericSearchingFunctionWithoutNullChecks;

	msgTypes: typeof MsgType[keyof typeof MsgType][] = Object.values(MsgType).sort((a, b) => +a.msgType - +b.msgType);
	allClients: AllClients[] = [];
	allClients$ = new Observable<AllClients[]>();
	allClientsRecord: Record<string, AllClients> = {};

	// allAgents: Users[] = [];
	// allAgents$ = new Observable<Users[]>();
	// allAgentsRecord: Record<string, Users> = {};
	allAgents$ = new BehaviorSubject<Users[]>([]);
	allAgentsDisplay$: Observable<Users[]>;
	allAgentsRecord$: Observable<Record<string, Users>> = this.allAgents$.pipe(map(arr => arr.reduce((p, c) => ({ ...p, [c.AgentID]: c }), {})));

	allDoctypes$ = new BehaviorSubject<DocTypeDefinitions[]>([]);
	allDoctypesDisplay$: Observable<DocTypeDefinitions[]>;
	allDoctypesRecord$: Observable<Record<string, DocTypeDefinitions>> = this.allDoctypes$.pipe(map(arr => arr.reduce((p, c) => ({ ...p, [c.DocType]: c }), {})));

	agentsToGet = [];

	// selectedProducts = new FormControl<Product[]>([], { nonNullable: true, validators: ValidateArrayLength });
	alertStatusControl = new FormControl<typeof activeArray[number]>(alertActivity.active, { nonNullable: true, validators: Validators.required });

	isEdittingAlert = false;
	selectedAlertID = null;

	isProductsPopupOpen = false;

	allowedCompanies = [1, 2];

	companyNum: number = 1;

	multiCompany = false;

	form = this.fb.nonNullable.group({
		Name: ['', Validators.required],
		MsgType: [<typeof MsgType[keyof typeof MsgType]['msgType']>Object.values(MsgType)[0].msgType, Validators.required],
		Msg: ['', Validators.required],
		Active: this.alertStatusControl,
		time: [''],
		clients: [<AllClients[]>[]],
		clientsSearch: [''],
		agents: [<Users[]>[]],
		agentsSearch: [''],
		doctypes: [<DocTypeDefinitions[]>[]],
		doctypesSearch: [''],
		percentagePerTeuda: [<number>null],
		shouldAffectAllClients: [true],
		shouldAffectAllAgents: [true],
		shouldAffectAllDoctypes: [true],
		shouldBlockAgent: [false],
		StartDate: [this.dates.min],
		EndDate: [this.dates.min],
	});

	shouldShowClientAutocomplete$ = new BehaviorSubject(false);
	shouldShowAgentAutocomplete$ = new BehaviorSubject(false);
	shouldShowDateSelection$ = new BehaviorSubject(false);
	shouldShowDoctypeSelection$ = new BehaviorSubject(false);

	showClientAutocomplete$ = this.form.controls.shouldAffectAllClients.valueChanges.pipe(map(value => value));
	showAgentAutocomplete$ = this.form.controls.shouldAffectAllAgents.valueChanges.pipe(map(value => value));
	showDoctypeAutocomplete$ = this.form.controls.shouldAffectAllDoctypes.valueChanges.pipe(map(value => value));

	shouldAllowClientSelection$ = new BehaviorSubject(false);
	shouldAllowAgentSelection$ = new BehaviorSubject(false);
	shouldAllowDoctypeSelection$ = new BehaviorSubject(false);
	shouldAllowTimeSelection$ = new BehaviorSubject(false);
	shouldAllowBlockSelection$ = new BehaviorSubject(false);
	shouldAllowPercentagePerTeuda$ = new BehaviorSubject(false);

	addProductPressed = false;

	shouldShowNoProductsError = false;

	isLoadingProgressBarShowing = false;

	isAlertDrawerOpen = false;

	isOnlyAgentAlerts = !location.pathname.includes('admin');

	shouldShowStatusSelection = !this.isOnlyAgentAlerts;

	openNewAlertOnInit = this.acr.snapshot.queryParams['newAlert'];

	historyBackTwice = this.acr.snapshot.queryParams['backTwice'];

	finishedSettingUpAllInformation$ = new BehaviorSubject<boolean>(false);

	// isAllocationPage = location.pathname.includes('allocation');
	// allocationPopupOpen = false;
	// selectedAllocationProduct = new FormControl<Product | string>('');

	ngOnInit(): void {
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			const CustomerInformation = localStorage.getItem('CustomerInformation');
			if (!CustomerInformation) {
				this.router.navigate(['login']);
				return;
			}

			this._ss.CustomerInformation = JSON.parse(decompress(CustomerInformation));
		}
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		if (this._ss.CustomerInformation.HasMultipleCompanies === '1') {
			this.multiCompany = true;
			this.companyNum = 0;
		}

		if (this.isOnlyAgentAlerts) {
			this.msgTypes = this.msgTypes.filter(msg => msg.msgType === '1000');
		}

		const clients = localStorage.getItem('AllClients');
		//const 	products = localStorage.getItem('AllProducts');

		if (
			!clients
			// || !products
		) {
			alert('אנא עדכן נתונים');
			this.router.navigate(['home']);
		}

		this.allClients = JSON.parse(decompress(clients));
		this.allClientsRecord = this.allClients.reduce((p, c) => {
			p[c.ClientId] = c;
			return p;
		}, {});

		this.setFormInteractivity();

		this.handleGetSubmanagersFirst();

		this.handlePopulateDoctypes();

		this._ss.CompanyNumForDuplicate = null;

		window.scrollTo(0, 0);

		this._ss.Betipul = 0;
		this._ss.TeudotDetailsEach = [];
		if (localStorage.getItem('UserName') !== null && localStorage.getItem('AgentId') !== null) {
			this._ss.UserNameConnected = localStorage.getItem('UserName');
			this._ss.AgentIdConnected = localStorage.getItem('AgentId');
		} else {
			this.router.navigate(['login']);
		}

		this.refreshAlerts();

		if (this.openNewAlertOnInit) {
			this.finishedSettingUpAllInformation$.pipe(takeUntil(this.destroy$)).subscribe({
				next: () => {
					this.openNewAlertDrawer();
				},
			});
		}
	}

	displayFnClients(client: AllClients) {
		return displayFn({ mode: 'clients', value: client });
	}

	displayFnAgents(agent: Users) {
		return displayFn({ mode: 'agents', value: agent });
	}

	displayFnDoctypes(doctype: DocTypeDefinitions) {
		return displayFn({ mode: 'doctypes', value: doctype });
	}

	chipClicked(i, mode: ArrayValues) {
		this.form.controls[mode].value.splice(i, 1);
		this.form.controls[mode + 'Search'].reset();
		this.form.controls[mode].updateValueAndValidity();
	}

	handleAutocompleteSelect(event: MatAutocompleteSelectedEvent, mode: ArrayValues): void {
		this.form.controls[mode].setValue([...this.form.controls[mode].value, event.option.value]);
		this.form.controls[mode + 'Search'].reset();
	}

	async handlePopulateDoctypes() {
		if (!this._doctype.doctypesDefinitions?.length) {
			const docsFromStorage = localStorage.getItem('DocTypesDefinitions');
			if (docsFromStorage) {
				this._doctype.doctypesDefinitions = JSON.parse(decompress(docsFromStorage));
				this.allDoctypes$.next(this._doctype.doctypesDefinitions);
				return;
			}
			try {
				const res = await firstValueFrom(this._ss.GetAllDoctypesDefinitions());
				if (typeof res === 'string') throw new Error('ConnectionError');
				this._doctype.doctypesDefinitions = res.recordset;
				localStorage.setItem('DocTypesDefinitions', compress(JSON.stringify(this._doctype.doctypesDefinitions)));
				this.allDoctypes$.next(this._doctype.doctypesDefinitions);
				return;
			} catch (error) {
				alert('לא ניתן להביא סוגי תעודות');
				this.form.controls.shouldAffectAllDoctypes.disable({ onlySelf: true });
				return;
			}
		}

		this.allDoctypes$.next(this._doctype.doctypesDefinitions);
	}

	handleGetSubmanagersFirst() {
		if (this._ss.CustomerInformation.Submanagers !== '1') {
			this.getAllUsers();
			return;
		}
		if (!this._ss.agentsOfSubmanager.length) {
			this._ss.getAdminAgents().subscribe({
				next: res => {
					if (typeof res == 'string') {
						alert('חלה תקלה בהבאת סוכנים עבור מנהל זה. נסו שנית');
						return;
					}

					this._ss.agentsOfSubmanager = this.agentsToGet = res.recordset.map(obj => obj.Agent);
					this.agentsToGet.push(this._ss.AgentIdConnected);
				},
				error: err => {
					alert('שגיאת שרת');
				},
			});
		} else {
			this.agentsToGet = this._ss.agentsOfSubmanager;
			this.getAllUsers();
		}
	}

	getAllUsers() {
		this._ss
			.GetAllAgents(this.agentsToGet)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					if (typeof res === 'string') {
						alert('ישנה תקלה בהבאת סוכנים, אנא נסו שנית מאוחר יותר');
						history.back();
						return;
					}
					// this.allAgents = res.recordset;
					this.allAgents$.next(res.recordset);

					// this.allAgentsRecord = this.allAgents.reduce((p, c) => {
					// 	p[c.AgentID] = c;
					// 	return p;
					// }, {});
					this.finishedSettingUpAllInformation$.next(true);
					this.finishedSettingUpAllInformation$.complete();
				},
			});
	}

	closeDrawer() {
		this.isEdittingAlert = false;
		this.form.reset();
		this.alertSuccess = false;
		this.form.enable();
		this.showHideFilterPopup(false);
	}

	refreshAlerts() {
		this._loading.changeLoadingStatuses({ isLoading: true });

		let alerts = this.allAlerts;
		if (this.isOnlyAgentAlerts) {
			alerts = alerts.filter(al => al.MsgType === '1000' && al.JSON.agents?.includes(this._ss.AgentIdConnected));
		}
		this.allAlerts$.next(alerts);

		console.log(this.allAlerts);
		this._loading.changeLoadingStatuses({ isLoading: false });
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();

		if (this.historyBackTwice) {
			history.go(-1);
		}
	}

	showHideFilterPopup(value): void {
		this.isAlertDrawerOpen = value;
	}

	getDate(val: 'min' | 'max') {
		if (val === 'min') {
			return new Date(this.dates.min);
		}
		return new Date(this.dates.max);
	}

	setFormInteractivity() {
		const {
			MsgType: MsgTypeControl,
			clients,
			agents,
			doctypes,
			time,
			shouldAffectAllClients,
			shouldAffectAllAgents,
			shouldAffectAllDoctypes,
			Active,
			StartDate,
			EndDate,
			percentagePerTeuda,
		} = this.form.controls;

		const shouldAffectAll = [
			{
				valueChange: shouldAffectAllClients,
				affected: clients,
				subject: this.shouldShowClientAutocomplete$,
			},
			{
				valueChange: shouldAffectAllAgents,
				affected: agents,
				subject: this.shouldShowAgentAutocomplete$,
			},
			{
				valueChange: shouldAffectAllDoctypes,
				affected: doctypes,
				subject: this.shouldShowDoctypeSelection$,
			},
		];

		MsgTypeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(type => {
			const msgType = MsgTypeOptions[type];

			const controlsArray: { condition: boolean; control: FormControl; updateValidity?: boolean; customValidators?: ValidatorFn[] }[] = [
				{ condition: msgType.clients && !shouldAffectAllClients.value, control: clients, customValidators: [ValidateArrayLength] },
				{ condition: msgType.agents && !shouldAffectAllAgents.value, control: agents, customValidators: [ValidateArrayLength] },
				{ condition: msgType.doctypes && !shouldAffectAllDoctypes.value, control: doctypes, customValidators: [ValidateArrayLength] },
				{ condition: msgType.selectProfit, control: percentagePerTeuda, customValidators: [Validators.pattern(/^\d+(.\d{1,3})?$/)], updateValidity: true },
			];

			for (const { condition, control, updateValidity, customValidators } of controlsArray) {
				if (condition) {
					let validators = [Validators.required];
					if (customValidators) {
						validators = validators.concat(customValidators);
					}
					control.setValidators(validators);
					if (updateValidity) {
						control.updateValueAndValidity();
					}
				} else {
					control.clearValidators();
					control.setErrors(null);
				}
			}

			if (msgType.timeSensitive) {
				time.setValidators(Validators.required);
				if (!time.value) {
					time.setErrors({ required: true });
				}
			} else {
				time.clearValidators();
				time.setErrors(null);
			}

			this.shouldAllowClientSelection$.next(MsgTypeOptions[type].clients);
			this.shouldAllowAgentSelection$.next(MsgTypeOptions[type].agents);
			this.shouldAllowTimeSelection$.next(MsgTypeOptions[type].timeSensitive);
			this.shouldAllowDoctypeSelection$.next(MsgTypeOptions[type].doctypes);
			this.shouldAllowBlockSelection$.next(MsgTypeOptions[type].block);
			this.shouldAllowPercentagePerTeuda$.next(MsgTypeOptions[type].selectProfit);
		});

		for (const { valueChange, affected, subject } of shouldAffectAll) {
			valueChange.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(boolean => {
				if (boolean) {
					affected.clearValidators();
					affected.reset();
				} else {
					affected.setValidators([Validators.required, ValidateArrayLength]);
					affected.updateValueAndValidity();
				}
				subject.next(!boolean);
			});
		}

		Active.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
			const boolean = value === '2';
			if (boolean) {
				StartDate.setValidators(Validators.required);
				EndDate.setValidators(Validators.required);
			} else {
				StartDate.clearValidators();
				EndDate.clearValidators();
			}
			this.shouldShowDateSelection$.next(boolean);
		});

		// merge(StartDate.valueChanges, EndDate.valueChanges)
		// 	.pipe(takeUntil(this.destroy$))
		// 	.subscribe(value => {
		// 		console.log(value);
		// 	});

		this.allClients$ = this.form.controls.clientsSearch.valueChanges.pipe(
			startWith(''),
			map(value => (value && typeof value === 'string' ? this._filter(value, this.allClients, ['Client_Name', 'ClientId']) : this.allClients.slice(0, 50))),
			map(value => value.filter(v => !clients.value.includes(v))),
		);

		this.allAgentsDisplay$ = combineLatest([this.form.controls.agentsSearch.valueChanges.pipe(startWith('')), this.allAgents$]).pipe(
			map(([value, agents]) => (value && typeof value === 'string' ? this._filter(value, agents, ['UserName', 'AgentID']) : agents)),
			map(value => value.filter(v => !agents.value.includes(v))),
		);

		this.allDoctypesDisplay$ = combineLatest([this.form.controls.doctypesSearch.valueChanges.pipe(startWith('')), this.allDoctypes$]).pipe(
			map(([value, doctypes]) => (value && typeof value === 'string' ? this._filter(value, doctypes, ['DocTypeName', 'DocType']) : doctypes)),
			map(value => value.filter(v => !doctypes.value.includes(v))),
		);
	}

	async handleFormSubmit() {
		this.form.markAllAsTouched();
		if (this.form.invalid) return;

		const { Active, Msg, MsgType, time, Name, agents, clients, shouldAffectAllAgents, shouldAffectAllDoctypes, shouldAffectAllClients, shouldBlockAgent, StartDate, EndDate, percentagePerTeuda } =
			this.form.value;

		const alertBody: AtLeast<Alert, 'Msg'> = {
			Name,
			Msg,
			Active,
			MsgType,
			CreateDate: new Date().toISOString(),
			JSON: {},
		};

		const setTimeZero = (date: Date): Date => {
			date.setHours(0);
			date.setMinutes(0);
			date.setSeconds(0);
			return date;
		};

		if (Active === '2') {
			const start = typeof StartDate === 'object' ? setTimeZero(StartDate).toISOString() : StartDate;
			const end = typeof EndDate === 'object' ? EndDate.toISOString() : EndDate;
			alertBody.JSON = { date: { start, end } };
		}

		if (MsgTypeOptions[MsgType].timeSensitive) {
			alertBody.JSON = { ...alertBody.JSON, time };
		}

		if (MsgTypeOptions[MsgType].clients && !shouldAffectAllClients) {
			// alertBody.JSON = { ...alertBody.JSON, clients };
			alertBody.JSON = { ...alertBody.JSON, clients: clients.map(client => client.ClientId) };
		}

		if (MsgTypeOptions[MsgType].agents && !shouldAffectAllAgents) {
			alertBody.JSON = { ...alertBody.JSON, agents: agents.map(agent => agent.AgentID) };
		}

		if (MsgTypeOptions[MsgType].block && shouldBlockAgent) {
			alertBody.JSON = { ...alertBody.JSON, block: true };
		}

		if (MsgTypeOptions[MsgType].doctypes && !shouldAffectAllDoctypes) {
			alertBody.JSON = { ...alertBody.JSON, doctypes: this.form.controls.doctypes.value.map(doctype => doctype.DocType) };
		}

		if (MsgTypeOptions[MsgType].selectProfit) {
			alertBody.JSON = { ...alertBody.JSON, selectProfit: percentagePerTeuda };
		}

		const body: AlertPostBody = { ...alertBody, JSON: JSON.stringify(alertBody.JSON) };

		if (this.isEdittingAlert) {
			await this.handlePatchAlert(this.selectedAlertID, body);
			return;
		}

		this._ss.postNewAlert(body).subscribe({
			next: res => {
				this._loading.changeLoadingStatuses({ isLoading: false });

				if (res === 'ConnectionError' || res === 'RequestError') {
					console.log(res);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.alertSuccess = false;
					return;
				}

				this.alertSuccess = true;

				this.allAlerts = this._alert.Alerts = res.recordset.map(al => ({ ...al, JSON: JSON.parse(al.JSON) }));

				this.form.reset();
				this.refreshAlerts();
				localStorage.setItem('Alerts', compress(JSON.stringify(this.allAlerts)));
			},
			error: err => {
				console.log(err);
				alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
				this._loading.changeLoadingStatuses({ isLoading: false });
				this.alertSuccess = false;
			},
		});
	}

	async handlePatchAlert(alertID: number, postBody: Partial<AlertPostBody>) {
		return new Promise<void>((resolve, reject) => {
			this._ss.patchAlert(alertID, postBody).subscribe({
				next: res => {
					this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.alertSuccess = false;
					this._loading.changeLoadingStatuses({ isLoading: false });

					reject();
				},
			});
		});
	}

	handleDeleteAlert(alertID: number) {
		return new Promise<void>((resolve, reject) => {
			this._ss.deleteAlert(alertID).subscribe({
				next: async res => {
					await this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					// this._loading.changeLoadingStatuses({ isLoading: false });
					this.alertSuccess = false;
					reject();
				},
			});
		});
	}

	handleDateChange(val: 'month' | 'year') {
		const start = new Date(this.form.controls.StartDate.value);

		if (val === 'month') {
			start.setMonth(start.getMonth() + 1);
		}

		const date = val === 'month' ? start : new Date(new Date(this.form.controls.StartDate.value).getTime() + 1000 * 60 * 60 * 24 * 365);
		this.form.controls.EndDate.setValue(date);
	}

	openNewAlertDrawer(formData: Required<typeof this.form.value> = { ...formDefaultValue, MsgType: Object.values(MsgType)[0]['msgType'] }) {
		this.showHideFilterPopup(true);
		if (this.isOnlyAgentAlerts) {
			this.allAgentsRecord$.pipe(take(1)).subscribe({
				next: record => {
					formData.agents = [record[this._ss.AgentIdConnected]];
					formData.Active = alertActivity.byDate;
					formData.shouldAffectAllAgents = false;
				},
			});
		}
		this.form.setValue(formData);
		setTimeout(() => {
			this.form.updateValueAndValidity();
		}, 0);
	}

	handleEditAlert(alert: Alert) {
		this.isEdittingAlert = true;
		this.selectedAlertID = alert.ID;

		const values = structuredClone(formDefaultValue);
		const alertObject = structuredClone(alert);

		values.Name = alertObject.Name;
		values.Active = alertObject.Active;
		values.Msg = alertObject.Msg;

		const { agents, clients, date, time, block, selectProfit } = alert.JSON;

		if (agents?.length) {
			this.allAgentsRecord$.pipe(take(1)).subscribe({
				next: record => {
					values.agents = agents.map(agent => record[agent]);
				},
			});
			values.shouldAffectAllAgents = false;
			this.shouldShowAgentAutocomplete$.next(true);
		}

		if (clients?.length) {
			values.clients = clients.map(client => this.allClientsRecord[client]);
			values.shouldAffectAllClients = false;
			this.shouldShowClientAutocomplete$.next(true);
		}

		if (date) {
			values.StartDate = new Date(date.start);
			values.EndDate = new Date(date.end);
			this.shouldShowDateSelection$.next(true);
		}

		if (time) {
			values.time = time;
		}

		if (block) {
			values.shouldBlockAgent = block;
		}

		if (selectProfit && isFinite(+selectProfit)) {
			values.percentagePerTeuda = +selectProfit;
		}

		this.openNewAlertDrawer({ ...values, MsgType: alert.MsgType });
	}

	async handleAfterHttpRequest(res: ResponseFromExpress<AlertInDatabase>, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void) {
		this._loading.changeLoadingStatuses({ isLoading: false });

		if (res === 'ConnectionError' || res === 'RequestError') {
			console.log(res);
			alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
			return reject();
		}

		this.alertSuccess = true;

		this.selectedAlertID = null;
		// this.isEdittingAlert = false;

		this.allAlerts = this._alert.Alerts = res.recordset.map(al => ({ ...al, JSON: JSON.parse(al.JSON) }));
		this.refreshAlerts();
		localStorage.setItem('Alerts', compress(JSON.stringify(this.allAlerts)));
		resolve();
	}

	async handleChangeAlert(
		e: MatButtonToggleChange | MouseEvent | PointerEvent | Event,
		alertID: number,
		action: 'patch' | 'delete' | 'patch-msg',
		options: { status?: string; input?: NgModel; i?: number } = {},
	) {
		if (this.isLoadingProgressBarShowing) return;

		const { status, i, input } = options;

		this.isLoadingProgressBarShowing = true;

		let msg = 'קרתה תקלה בתקשורת נתונים, אנא נסו שנית';

		if (action === 'patch' && e instanceof MatButtonToggleChange) {
			const newStatus = e.value;
			e.source.buttonToggleGroup.value = status;
			try {
				// await this.handlePatchAlert(alertID, { Active: newStatus });
				e.source.buttonToggleGroup.value = newStatus;
				msg = 'סטטוס התראה הוחלף בהצלחה';
				this.alertSuccess = false;
			} catch (error) {}
		}

		if (action === 'delete' && !(e instanceof MatButtonToggleChange)) {
			try {
				await this.handleDeleteAlert(alertID);
				msg = 'התראה נמחקה בהצלחה';
				this.alertSuccess = false;
			} catch (error) {}
		}

		if (action === 'patch-msg' && !(e instanceof MatButtonToggleChange)) {
			const newMsg = input.value;
			const alert = this.allAlerts$.value[i];
			console.log(alert);
			if (!newMsg || newMsg === alert.Msg) {
				return;
			}

			try {
				await this.handlePatchAlert(alert.ID, { Msg: newMsg });

				input.reset();

				this.alertSuccess = false;

				msg = 'מלל התראה הוחלף בהצלחה';
			} catch (error) {}
		}

		this._bottomPopup.show(msg, { action: '' });

		this.isLoadingProgressBarShowing = false;
	}

	BackToDohot() {
		history.back();
	}
}
